import React from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';
import { FavoriteUser } from '@/hooks/use-favorite-users';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

interface FavoriteUserCardProps {
  favorite: FavoriteUser | null;
  position: number;
  onClick?: () => void;
  unreadCount?: number;
}

const FavoriteUserCard = ({ favorite, position, onClick, unreadCount = 0 }: FavoriteUserCardProps) => {
  if (!favorite) {
    return (
      <div className="relative w-full h-full rounded-lg border border-dashed border-muted-foreground/30 bg-muted/20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 rounded-full bg-muted-foreground/20 flex items-center justify-center mx-auto mb-2">
            <span className="text-sm font-bold text-muted-foreground">{position}</span>
          </div>
          <p className="text-xs text-muted-foreground">Empty</p>
        </div>
      </div>
    );
  }

  const hasRecentPost = !!favorite.recent_bulletin;
  const imageUrl = favorite.recent_bulletin?.image_url || favorite.recent_bulletin?.thumbnail_url;

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={cn(
        "relative w-full h-full rounded-lg overflow-hidden cursor-pointer",
        "bg-gradient-to-br from-primary/10 via-accent/10 to-secondary/10",
        "border border-border/50 hover:border-primary/30 transition-all duration-200",
        unreadCount > 0 && "ring-2 ring-primary ring-offset-2 ring-offset-background"
      )}
    >
      {/* Position Badge */}
      <div className="absolute top-2 left-2 z-20">
        <div className="w-6 h-6 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center">
          <span className="text-xs font-bold text-white">{position}</span>
        </div>
      </div>

      {/* Unread Count Badge */}
      {unreadCount > 0 && (
        <div className="absolute top-2 right-2 z-20">
          <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
            <span className="text-xs font-bold text-white">{unreadCount}</span>
          </div>
        </div>
      )}

      {/* Background Content */}
      <div className="absolute inset-0">
        {hasRecentPost && imageUrl ? (
          <img
            src={imageUrl}
            alt="Recent post"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-muted/80 to-muted/40 flex items-center justify-center">
            <div className="text-center">
              <Clock className="w-6 h-6 text-muted-foreground mx-auto mb-1" />
              <p className="text-xs text-muted-foreground">No recent posts</p>
            </div>
          </div>
        )}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

      {/* User Info */}
      <div className="absolute bottom-0 left-0 right-0 p-3 z-10">
        <div className="flex items-center gap-2">
          <Avatar className="w-8 h-8 border-2 border-white/80">
            <AvatarImage src={favorite.profile.profile_image_url || undefined} />
            <AvatarFallback className="text-xs">
              {favorite.profile.username[0].toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-white truncate">
              {favorite.profile.display_name || `@${favorite.profile.username}`}
            </p>
            {hasRecentPost && (
              <p className="text-xs text-white/70 truncate">
                {favorite.recent_bulletin?.content}
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FavoriteUserCard;